import { useEffect, useMemo, useState } from "react";
import { formatCurrency, formatDurationMinutes } from "../adminUtils";
import { getAdminDashboard } from "../../../api/adminDashboard";

const useAdminDashboard = ({ t }) => {
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState("");

  const dashboardMetrics = dashboard?.metrics;
  const dashboardCurrency = dashboard?.currency || "EUR";

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      setDashboardLoading(true);
      setDashboardError("");
      try {
        const payload = await getAdminDashboard();
        if (cancelled) return;
        setDashboard(payload);
      } catch (error) {
        if (cancelled) return;
        setDashboardError(error?.message || "Failed to load dashboard.");
        setDashboard(null);
      } finally {
        if (!cancelled) setDashboardLoading(false);
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, []);

  const refreshDashboard = async ({ silent = false } = {}) => {
    if (!silent) setDashboardLoading(true);
    setDashboardError("");
    try {
      const payload = await getAdminDashboard();
      setDashboard(payload);
    } catch (error) {
      setDashboardError(error?.message || "Failed to load dashboard.");
      setDashboard(null);
    } finally {
      if (!silent) setDashboardLoading(false);
    }
  };

  const glanceCards = useMemo(() => {
    if (!dashboardMetrics) return [];
    return [
      {
        id: "today-bookings",
        label: t("admin.home.todayBookings", "Today's bookings"),
        value: dashboardMetrics.todayBookingsCount,
        detail: t("admin.home.today", "Today"),
        tone: "mint",
      },
      {
        id: "equipment-in-use",
        label: t("admin.home.equipmentInUse", "Equipment in use"),
        value: dashboardMetrics.equipmentInUseCount,
        detail: t("admin.home.itemsCheckedOut", "Items checked out"),
        tone: "sun",
      },
      {
        id: "avg-booking-time",
        label: t("admin.home.avgBookingTime", "Average booking time"),
        value: formatDurationMinutes(dashboardMetrics.averageBookingDurationMinutes),
        detail: t("admin.home.avgBookingTimeDetail", "Today"),
        tone: "coral",
      },
      {
        id: "avg-booking-price",
        label: t("admin.home.avgBookingPrice", "Average booking price"),
        value: formatCurrency(dashboardMetrics.averageBookingPriceToday, dashboardCurrency),
        detail: t("admin.home.avgBookingPriceDetail", "Today"),
        tone: "ink",
      },
      {
        id: "profit-today",
        label: t("admin.home.profitToday", "Profit today"),
        value: formatCurrency(dashboardMetrics.profitToday, dashboardCurrency),
        detail: t("admin.home.profitTodayDetail", "Revenue minus refunds"),
        tone: "ink",
      },
    ];
  }, [dashboardCurrency, dashboardMetrics, t]);

  const alertCards = useMemo(() => {
    if (!dashboardMetrics) return [];
    return [
      {
        id: "overdue-returns",
        title: t("admin.alerts.overdue", "Overdue returns"),
        value: dashboardMetrics.overdueReturnsCount,
        severity: "critical",
        detail: t("admin.alerts.overdueDetail", "Bookings past due"),
      },
      {
        id: "cancelled-bookings",
        title: t("admin.alerts.cancelled", "Cancelled bookings"),
        value: dashboardMetrics.cancelledBookingsCount,
        severity: "warning",
        detail: t("admin.alerts.cancelledDetail", "Cancelled today"),
      },
      {
        id: "failed-payments",
        title: t("admin.alerts.failedPayments", "Failed payments"),
        value: dashboardMetrics.failedPaymentsCount,
        severity: "attention",
        detail: t("admin.alerts.failedDetail", "Failed today"),
      },
    ];
  }, [dashboardMetrics, t]);

  return {
    dashboard,
    dashboardLoading,
    dashboardError,
    dashboardCurrency,
    glanceCards,
    alertCards,
    refreshDashboard,
  };
};

export default useAdminDashboard;
